/**
 * Reading an applicant's stored answers back out for the admin review
 * surface (LG-414).
 *
 * Everything here is driven by the `glance` / `lens` metadata on
 * `QUESTION_STEPS` rather than by a second list of field names — the
 * wizard's questions change as data edits, and a hard-coded copy in the
 * admin page would rot silently the first time one is renamed. One exception
 * names fields: `LEVEL_LINE_FIELDS` names three because the line it builds
 * gives them roles, and a test asserts they still exist. The names older rows
 * still hold live on the question itself, as `formerNames`.
 *
 * Answers are stored as jsonb keyed by question-step id, so every read has to
 * survive a missing step, a missing field, and a chip group that was stored
 * as an array. None of those are errors: a half-finished wizard row has
 * almost nothing, and it still has to render.
 */
import {
  otherAnswerName,
  storedNamesOf,
  withOtherText,
} from './other-answer.js'
import {
  type AuditionAnswer,
  type AuditionField,
  getQuestionStep,
  QUESTION_STEPS,
} from './steps.js'
import { isProseField, WIDGETS } from './widgets.js'

/** The shape of `AuditionSubmission.answers`: step id → field name → value. */
export type AuditionAnswers = Record<string, Record<string, AuditionAnswer>>

/** One scannable fact for the "At a glance" card. */
export interface GlanceFact {
  label: string
  value: string
  lens: 'level' | 'motivation' | null
}

/** One question and its answer, for the prose rendering of an application. */
export interface AnswerProseGroup {
  id: string
  title: string
  items: { name: string; question: string; answer: string }[]
}

/**
 * One answer as a display string. A multi-chip group joins with commas; an
 * unanswered or absent field is '' rather than undefined so callers can test
 * it as falsy without also swallowing a legitimate `0`-like value (there are
 * none — every answer is text).
 */
export function answerText(
  answers: AuditionAnswers | null | undefined,
  fieldName: string,
): string {
  const hit = resolveField(fieldName)
  if (!hit) return ''
  return answerDisplayParts(answers, hit.stepId, hit.field).join(', ')
}

/**
 * One answer as the shortest thing worth scanning — the widget's own
 * `lensText` where it declares one, the display string otherwise (LG-1052).
 * (A field's `glance` is something else: its label on the facts card.)
 *
 * The timeline stores a whole plan and reads for the lens as its deadline;
 * nothing here knows that, or needs to. Same tolerance as
 * {@link fieldDisplayParts}: a row holding an older shape of the question
 * reads as itself.
 */
export function fieldLensText(
  answers: AuditionAnswers | null | undefined,
  fieldName: string,
): string {
  const hit = resolveField(fieldName)
  if (!hit) return ''
  const value = storedAnswer(answers, hit.stepId, fieldName)
  const widget = WIDGETS[hit.field.type]
  if (widget.lensText && widget.is(value)) {
    return widget.lensText(value, hit.field)
  }
  return fieldDisplayParts(hit.field, value).join(', ')
}

/**
 * The question a field name belongs to, and the step it is asked on. Field
 * names are unique wizard-wide (asserted in the steps tests), so the first
 * hit is the only one.
 */
function resolveField(
  fieldName: string,
): { stepId: string; field: AuditionField } | undefined {
  for (const step of QUESTION_STEPS) {
    const field = step.fields.find((f) => f.name === fieldName)
    if (field) return { stepId: step.id, field }
  }
  return undefined
}

/**
 * One question's answer as the pieces every read-back surface shows: found in
 * whichever step bag holds it, under one of its `formerNames` when the current
 * one has nothing, and with an Other chip carrying what was typed beside it.
 * The Other text is read under the same name as the answer it sits beside,
 * because the two were written together.
 *
 * Former names are display only, never seeding: an answer stored under an old
 * name may not be a valid answer to today's question. `referral` was free
 * text, and a sentence seeded into a chip group is an option nobody can pick
 * — it would hold Next on a question that is optional. So `stepDefaults`
 * does not read them, and the applicant answers the new question fresh.
 */
export function answerDisplayParts(
  answers: AuditionAnswers | null | undefined,
  stepId: string,
  field: AuditionField,
): string[] {
  const held = storedNamesOf(field).find(
    ({ name }) => storedAnswer(answers, stepId, name) !== undefined,
  )
  const parts = fieldDisplayParts(
    field,
    held && storedAnswer(answers, stepId, held.name),
  )
  if (!held?.other) return parts
  return withOtherText(parts, storedAnswer(answers, stepId, held.other))
}

/**
 * One field's stored answer, from whichever step's bag actually holds it.
 *
 * Answers are keyed by the step the question was asked on, so a question that
 * MOVES between steps strands every row written before the move: the answer
 * is still there, under the old step id, and a strict read renders a blank
 * where evidence is. `openToMoves` and `interviewTimeline` moved from
 * Background to Goals, which is exactly the case LG-1032's tolerant-read-path
 * constraint is about — a question changing shape must never silently blank
 * an answer somebody already gave.
 *
 * Today's step wins when both bags hold the key, so a row answered again
 * after the move reads as the newer answer. The fallback scan is safe because
 * field names are unique across the whole wizard — asserted in the steps
 * tests, because this function is what makes that a correctness requirement
 * rather than a tidiness one.
 */
export function storedAnswer(
  answers: AuditionAnswers | null | undefined,
  stepId: string,
  fieldName: string,
): AuditionAnswer | undefined {
  if (!answers) return undefined
  const here = answers[stepId]?.[fieldName]
  if (here !== undefined) return here
  for (const [id, bag] of Object.entries(answers)) {
    if (id === stepId) continue
    // Guarded like the read above: the column's type is a claim nothing
    // checks, and a bag a backfill wrote as null must render blank, not throw.
    const there = bag?.[fieldName]
    if (there !== undefined) return there
  }
  return undefined
}

/**
 * Every answer from the steps asked BEFORE this one, flattened by field name.
 *
 * What a widget is allowed to read from outside its own step. A ladder takes
 * its rungs from `currentLevel`, which is asked on Background while the
 * ladder is now asked on Goals, so "the fields asked before this one" had to
 * stop meaning "on this step". Earlier only, never later: a widget that could
 * read an answer the applicant has not reached yet would settle against a
 * value that is about to change under it.
 *
 * Flat by field name, not nested by step, because that is the shape the
 * widget contract already takes and the names are unique wizard-wide anyway.
 * Each read goes through {@link storedAnswer}, so this module has ONE rule
 * for where a stored answer lives: a source field that moves steps must keep
 * feeding the widgets that read it, exactly as it keeps showing on the recap.
 */
export function answersBeforeStep(
  stepId: string,
  answers: AuditionAnswers | null | undefined,
): Record<string, AuditionAnswer | undefined> {
  const earlier: Record<string, AuditionAnswer | undefined> = {}
  if (!answers) return earlier
  for (const step of QUESTION_STEPS) {
    if (step.id === stepId) break
    for (const field of step.fields)
      earlier[field.name] = storedAnswer(answers, step.id, field.name)
  }
  return earlier
}

/**
 * What {@link QuestionStep} seeds a step's fields from — the same tolerance
 * the admin recap gets, so an applicant resuming a draft written before a
 * question moved still finds their own answer in the box.
 */
export function stepDefaults(
  stepId: string,
  answers: AuditionAnswers | null | undefined,
): Record<string, AuditionAnswer | undefined> {
  const step = getQuestionStep(stepId)
  if (!step) return {}
  const seeded: Record<string, AuditionAnswer | undefined> = {}
  for (const field of step.fields) {
    seeded[field.name] = storedAnswer(answers, stepId, field.name)
    if (field.other) {
      const name = otherAnswerName(field)
      seeded[name] = storedAnswer(answers, stepId, name)
    }
  }
  return seeded
}

/**
 * One stored answer as the pieces it should be shown as, in the field's own
 * vocabulary — the ladder stores move ids, and "up" is not something to print
 * at a reviewer.
 *
 * Returns the parts rather than a joined string because the applicant-facing
 * recap and the admin surface punctuate a list differently, and a `separator`
 * argument would just be that difference wearing a disguise.
 *
 * Unrecognised values pass through rather than dropping out: an answer stored
 * under an older shape of the question is still evidence.
 */
export function fieldDisplayParts(
  field: AuditionField,
  value: AuditionAnswer | undefined,
): string[] {
  const widget = WIDGETS[field.type]
  // Rows written while this question was a different widget hold that older
  // shape — a chip string like 'Within 3 months' where a timeline now sits.
  // That is still what they told us, so it renders as itself rather than
  // disappearing behind the new shape. The tolerance lives here rather than
  // in each descriptor, so a widget invented later inherits it.
  if (!widget.is(value))
    return typeof value === 'string' && value !== '' ? [value] : []
  return widget.summary(value, field)
}

/** Every field that declared a `glance` label, in wizard order. */
export function glanceFacts(
  answers: AuditionAnswers | null | undefined,
): GlanceFact[] {
  const facts: GlanceFact[] = []
  for (const step of QUESTION_STEPS) {
    for (const field of step.fields) {
      if (!field.glance) continue
      facts.push({
        label: field.glance,
        value: answerText(answers, field.name),
        lens: field.lens ?? null,
      })
    }
  }
  return facts
}

/**
 * The free-text answer that speaks to a lens — the motivation quote pulled
 * out and shown large when the reviewer is judging on motivation.
 *
 * Only prose fields qualify: a field the applicant picked or drew, tagged
 * `lens: 'level'`, is a fact for the glance card rather than something to
 * quote back at anybody.
 */
export function lensAnswer(
  answers: AuditionAnswers | null | undefined,
  lens: 'level' | 'motivation',
): string {
  for (const step of QUESTION_STEPS) {
    for (const field of step.fields) {
      if (field.lens !== lens || !isProseField(field)) continue
      const text = answerText(answers, field.name)
      if (text) return text
    }
  }
  return ''
}

/**
 * The three fields the Level lens line is built from, by name.
 *
 * This is the module's one exception to "never a second list of field
 * names", and it is deliberate: the line has a shape — `from → to · when` —
 * so the three answers play named roles that `lens: 'level'` metadata alone
 * cannot assign without reading meaning into list position.
 *
 * The exception is only safe because it is loud: `levelFieldsExist` below is
 * asserted in this module's tests, so renaming one of these in `steps.ts`
 * fails the build instead of quietly rendering "— → —" on every queue row.
 */
export const LEVEL_LINE_FIELDS = {
  current: 'currentLevel',
  target: 'targetLevel',
  timeline: 'interviewTimeline',
} as const

/** True when every {@link LEVEL_LINE_FIELDS} name is still a real question. */
export function levelFieldsExist(): boolean {
  const known = new Set(
    QUESTION_STEPS.flatMap((step) => step.fields.map((field) => field.name)),
  )
  return Object.values(LEVEL_LINE_FIELDS).every((name) => known.has(name))
}

/**
 * Every key in a hand-written answers bag that names no question we ask, as
 * `step` ids and `step.field` paths.
 *
 * The other half of the same exception. A bag written by hand — the seed
 * script's, today — has plain string keys checked against nothing: rename a
 * field in `QUESTION_STEPS` and it still compiles, then writes rows whose
 * admin recap prints a blank where an answer should be. Asserted in the
 * writer's own tests, the rename fails the build instead.
 *
 * Says nothing about MISSING answers: a half-finished application is a real
 * row, and demanding every question here would make this fail on the thing
 * it exists to allow.
 */
export function unknownAnswerFields(answers: AuditionAnswers): string[] {
  const unknown: string[] = []
  for (const [stepId, fields] of Object.entries(answers)) {
    const step = getQuestionStep(stepId)
    if (!step) {
      unknown.push(stepId)
      continue
    }
    // Only the names the wizard still WRITES — narrower than `storedNamesOf`
    // on purpose. A former name is read back from old rows, never written
    // again, so a hand-written bag using one is exactly what this reports.
    for (const name of Object.keys(fields))
      if (
        !step.fields.some(
          (field) =>
            field.name === name ||
            (field.other !== undefined && otherAnswerName(field) === name),
        )
      )
        unknown.push(`${stepId}.${name}`)
  }
  return unknown
}

/**
 * One row of the queue's lens cell, in characters — everything the row shows,
 * the ellipsis a clipped line ends with included. A clipped line is therefore
 * this long and never one character more, which is the same number the
 * widgets' own lens text is held to. Both lens lines share it: the level line
 * had no clip at all, which is how the timeline recap grew it past the cell
 * without anything noticing (LG-1052).
 */
export const LENS_LINE_MAX_LENGTH = 92

/** One row's worth of a lens line, ellipsised past {@link LENS_LINE_MAX_LENGTH}. */
function clipLensLine(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text
  // The marker occupies a character of the row like any other, so it comes
  // out of the budget rather than sitting one past it. Clamped because the
  // length guard above lets a budget of 0 through, and a bare `maxLength - 1`
  // would then read as `slice(0, -1)` — the whole essay back, minus its last
  // character, which is the opposite of clipping.
  return `${text.slice(0, Math.max(0, maxLength - 1)).trimEnd()}…`
}

/**
 * The one-line level summary shown under each queue row when the Level lens
 * is active: where they are, where they want to be, and when.
 *
 * "When" is the timeline's lens text — the plan's deadline alone, not its full
 * recap: the recap is the length of the whole cell, and the queue row
 * truncates, so it was the "when" that got clipped off — the third slot the
 * lens exists to carry. Rows written before the timeline widget still hold a
 * chip string, which is short and is quoted as it is.
 */
export function levelLine(
  answers: AuditionAnswers | null | undefined,
  maxLength = LENS_LINE_MAX_LENGTH,
): string {
  const level = answerText(answers, LEVEL_LINE_FIELDS.current) || '—'
  const target = answerText(answers, LEVEL_LINE_FIELDS.target) || '—'
  const timeline = fieldLensText(answers, LEVEL_LINE_FIELDS.timeline)
  const ladder = `${level} → ${target}`
  return clipLensLine(timeline ? `${ladder} · ${timeline}` : ladder, maxLength)
}

/**
 * The Motivation-lens equivalent: their own words, clipped to one row.
 *
 * Truncation happens on the server so the row payload stays small — the queue
 * carries one of these per applicant and never needs the full essay until the
 * reviewer opens the detail page.
 */
export function motivationLine(
  answers: AuditionAnswers | null | undefined,
  maxLength = LENS_LINE_MAX_LENGTH,
): string {
  const text = lensAnswer(answers, 'motivation').replace(/\s+/g, ' ').trim()
  if (!text) return ''
  return clipLensLine(text, maxLength)
}

/**
 * Every non-glance answer as prose, grouped by wizard step — the reviewer's
 * read-through of the application.
 *
 * Groups with nothing in them are dropped rather than rendered as empty
 * headings, so a step made entirely of glance facts does not leave a bare
 * title on the page.
 */
export function answerProseGroups(
  answers: AuditionAnswers | null | undefined,
): AnswerProseGroup[] {
  return QUESTION_STEPS.map((step) => ({
    id: step.id,
    title: step.title,
    items: step.fields
      .filter((field) => !field.glance)
      .map((field) => ({
        name: field.name,
        question: field.label,
        answer: answerText(answers, field.name),
      })),
  })).filter((group) => group.items.length > 0)
}
